import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Eye, FileText, ChevronDown, PlayCircle, Info, ExternalLink } from 'lucide-react';
import Shell from '../../components/Shell';
import Seo from '../../components/Seo';
import { Section, CTA } from '../../components/Pieces';
import Modal from '../../components/Modal';
import ProjectIcon from './ProjectIcon';
import { wcddProject, satnavariProject } from '../../data/projectsData';

export function WcddHaryana() {
  return (
    <ProjectDetail
      project={wcddProject}
      seoDescription="Haryana WCD Department 67-centre Digital Anganwadi Pilot — overview, project completion and evidencing artefacts, worker training and feedback documentation, and field videos."
      seoImage="/video-thumbs/video-06.jpg"
    />
  );
}

export function SatnavariDigitalAnganwadi() {
  return (
    <ProjectDetail
      project={satnavariProject}
      seoDescription="Satnavari, Maharashtra Digital Anganwadi Initiative — India's first Smart Intelligent Village early childhood deployment, with access to the project video library."
      seoImage="/video-thumbs/video-02.jpg"
    />
  );
}

function ProjectDetail({ project, seoDescription, seoImage }) {
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [open, setOpen] = useState(null);

  return (
    <Shell>
      <Seo title={project.title} description={seoDescription} image={seoImage} />

      <section className="relative overflow-hidden bg-[var(--mm-cream)] border-b border-[var(--mm-line)]">
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] mm-dot-halo-light rounded-full opacity-60" />
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-20 relative">
          <BackToProjects />
          <div className="mt-8">
            <span className="inline-flex w-14 h-14 rounded-2xl bg-[#F3E8FA] text-[var(--mm-purple)] items-center justify-center">
              <ProjectIcon name={project.icon} />
            </span>
          </div>
          <h1 className="mt-6 font-display text-3xl md:text-5xl font-bold text-[var(--mm-ink)] max-w-4xl leading-[1.08]">{project.title}</h1>
          <p className="mt-6 text-base md:text-lg text-[var(--mm-ink)]/70 max-w-4xl leading-relaxed">{project.overview}</p>
        </div>
      </section>

      <Section bg="white">
        <div className="max-w-3xl">
          <div className="mm-kicker mb-3">Evidence</div>
          <h2 className="font-display text-2xl md:text-4xl font-bold text-[var(--mm-ink)] mm-rule">Project Completion &amp; Evidencing Artefacts</h2>
          <p className="mt-6 text-[var(--mm-ink)]/70 leading-relaxed">{project.artefactsIntro}</p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="artefacts-grid">
          {project.artefacts.map((a) => (
            <ArtefactCard key={a.id} artefact={a} expanded={open === a.id}
              onToggle={() => setOpen(open === a.id ? null : a.id)}
              onView={setDoc}
              onVideos={() => navigate('/projects/videos')} />
          ))}
        </div>
      </Section>

      {project.highlights && (
        <Section bg="cream2">
          <div className="max-w-2xl">
            <div className="mm-kicker mb-3">Project Highlights</div>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-[var(--mm-ink)] mm-rule">What the pilot covered.</h2>
          </div>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="project-highlights">
            {project.highlights.map((h) => (
              <div key={h.label} className="mm-card p-7 text-center">
                <div className="font-display text-3xl md:text-5xl font-bold text-[var(--mm-purple)]">{h.value}</div>
                <div className="mt-3 text-sm font-semibold text-[var(--mm-ink)]">{h.label}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section bg="white" className="!py-0">
        <BackToProjects />
      </Section>

      <CTA title="Request the complete evidence pack." primary={{ label: 'Contact Mashmari', href: '/contact' }} />

      {doc && (
        <Modal wide title={doc.name} onClose={() => setDoc(null)} testid="doc-modal">
          <iframe src={docViewerSrc(doc)} title={doc.name} className="w-full h-[70vh] border-0" />
          <div className="flex flex-wrap gap-3 px-5 md:px-7 py-4 border-t border-[var(--mm-line)]">
            <a href={doc.url} target="_blank" rel="noreferrer" className="btn-primary text-xs" data-testid="doc-modal-open">
              Open in new tab <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <a href={doc.url} download className="btn-outline text-xs" data-testid="doc-modal-download">
              Download file <Download className="w-3.5 h-3.5" />
            </a>
          </div>
        </Modal>
      )}
    </Shell>
  );
}

function docViewerSrc(doc) {
  const isDocx = /\.docx?$/i.test(doc.name || '');
  if (isDocx) {
    const abs = `${window.location.origin}${doc.url}`;
    return `https://docs.google.com/viewer?url=${encodeURIComponent(abs)}&embedded=true`;
  }
  return doc.url;
}

function BackToProjects() {
  return (
    <Link to="/projects" data-testid="back-to-projects"
      className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--mm-purple)] hover:gap-3 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mm-purple)] rounded-full">
      <ArrowLeft className="w-4 h-4" /> Back to Projects
    </Link>
  );
}

function ArtefactCard({ artefact, expanded, onToggle, onView, onVideos }) {
  const a = artefact;
  const isVideos = a.action === 'videos';
  const allDownloadable = (a.files || []).filter(f => f.url && !f.url.includes('monitoring.mashmari.in'));
  const claimed = parseInt(a.meta, 10);
  const downloadLabel = allDownloadable.length === 0
    ? null
    : (Number.isFinite(claimed) && claimed > allDownloadable.length
      ? `Download available file (1 of ${claimed})`
      : allDownloadable.length > 1 ? 'Download All' : 'Download');

  return (
    <article className="bg-white border border-[var(--mm-line)] rounded-2xl p-6 flex flex-col transition-all hover:shadow-[0_18px_40px_-26px_rgba(74,18,89,.35)]"
      data-testid={`artefact-${a.id}`}>
      <span className="inline-flex self-start items-center text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--mm-purple)] bg-[#F3E8FA] rounded-full px-3 py-1">{a.tag}</span>
      <h4 className="mt-4 font-display text-lg font-bold text-[var(--mm-ink)] leading-snug">{a.title}</h4>
      <p className="mt-2 text-xs text-[var(--mm-ink)]/55">{a.meta}</p>

      {a.note && (
        <p className="mt-3 flex gap-2 text-[11px] text-amber-700 leading-relaxed">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {a.note}
        </p>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        {isVideos ? (
          <button onClick={onVideos} className="btn-primary text-xs" data-testid={`artefact-videos-${a.id}`}>
            View videos <PlayCircle className="w-3.5 h-3.5" />
          </button>
        ) : (
          <>
            {a.action === 'view' && (
              <a href={a.viewUrl} target="_blank" rel="noreferrer" className="btn-primary text-xs" data-testid={`artefact-view-${a.id}`}>
                View dashboard <Eye className="w-3.5 h-3.5" />
              </a>
            )}
            {downloadLabel && (
              <a href={allDownloadable[0].url} download className="btn-primary text-xs" data-testid={`artefact-download-${a.id}`}>
                {downloadLabel} <Download className="w-3.5 h-3.5" />
              </a>
            )}
            {a.files?.length > 0 && (
              <button onClick={onToggle} aria-expanded={expanded} data-testid={`artefact-toggle-${a.id}`}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2.5 rounded-full border border-[var(--mm-line)] hover:border-[var(--mm-purple)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mm-purple)]">
                Files <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
              </button>
            )}
          </>
        )}
      </div>

      {expanded && a.files?.length > 0 && (
        <ul className="mt-4 pt-4 border-t border-[var(--mm-line)] space-y-3" data-testid={`artefact-files-${a.id}`}>
          {a.files.map((f) => (
            <li key={f.name} className="text-xs">
              <div className="flex gap-2 text-[var(--mm-ink)]/80 break-all">
                <FileText className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[var(--mm-purple)]" />
                <span>{f.name}</span>
              </div>
              <div className="mt-2 flex gap-3 pl-5">
                {f.url.includes('monitoring.mashmari.in') ? (
                  <a href={f.url} target="_blank" rel="noreferrer" className="font-semibold text-[var(--mm-purple)] hover:underline">Open dashboard</a>
                ) : (
                  <>
                    <button onClick={() => onView(f)} className="font-semibold text-[var(--mm-purple)] hover:underline" data-testid={`file-view-${a.id}`}>View</button>
                    <a href={f.url} download className="font-semibold text-[var(--mm-purple)] hover:underline" data-testid={`file-download-${a.id}`}>Download</a>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
